import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { usePagination } from '../hooks/usePagination';
import { usePrescriptionOperations } from '../hooks/usePrescriptionOperations';

interface PrescriptionListOptions {
  search?: string;
}

type TProps = {
  children: ReactNode;
};

type TContext = {
  selectedPrescription: TPrescription | null;
  limit: number;
  offset: number;
  prescriptionsList: TPrescriptionsList;
  prescriptionDetail: TPrescription | null;
  alertMsgs: Partial<Record<string, string[]>>;
  prescriptionsLoading: boolean;
  setOffset: Dispatch<SetStateAction<number>>;
  setLimit: Dispatch<SetStateAction<number>>;
  setSelectedPrescription: Dispatch<SetStateAction<TPrescription | null>>;
  getPrescriptionDetail: (id: string) => void;
  getPrescriptionList: (options?: PrescriptionListOptions) => Promise<void>;
};

export type TPrescription = {
    archived_stamp: Date;
    cancelled_stamp: Date;
    created_stamp: string;
    his_created_date: string;
    his_hospital_pharmacy_stock_id: number;
    his_status: string;
    id: string;
    status: string;
    modified_stamp: Date;
    prescription_created_stamp: string;
    prescription_id: number;
    details: TPrescriptionDetail[];
};

export type TPrescriptionsList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TPrescription[];
  last_updated_stamp?: Date;
};

export type TCreatePrescriptionProp = {
  model: number | string;
  name: string;
  location: string;
  config: {
      ip_address: string;
      port: number;
      description?: string;
      kiro_prescription_type?: number;
      kiro_prescription_id?: number;
  };
};

export type TPrescriptionDetalItem = {
    id: string;
    item_id: string;
    medicine_id: number;
    appointment_date: string;
    medicine_name: string;
    dosage: string;
    dosage_unit: string;
    amount_in_package: string;
    package_unit: string;
    is_final_container: boolean;
};

export type TPrescriptionDetail = {
  id: string;
  his_prescription_details_id: number;
  patient_first_name: string;
  patient_last_name: string;
  patient_mrn: number;
  patient_date_of_birth: string;
  prescription_name: string;
  route: string;
  administration_speed: string;
  items: TPrescriptionDetalItem[];
  status: string;
  appoint_date: string;
  final_container: string;
};

const PrescriptionContext = createContext<TContext>({} as TContext);

export const PrescriptionProvider = ({ children }: TProps) => {
  const [selectedPrescription, setSelectedPrescription] = useState<TPrescription | null>(null);
  const [alertMsgs, setAlertMessages] = useState<Partial<Record<string, string[]>>>({});
  const { limit, offset, setLimit, setOffset } = usePagination(10);
  const {
      prescriptionsList,
      prescriptionDetail,
      prescriptionsLoading,
      getPrescriptionList: fetchPrescriptionList,
      getPrescriptionDetail: fetchPrescriptionDetail,
  } = usePrescriptionOperations();

  const getPrescriptionList = async ({ search }: PrescriptionListOptions = {}) => {
      await fetchPrescriptionList({ search }, limit, offset, setAlertMessages);
  };

  const getPrescriptionDetail = async (id: string) => {
      await fetchPrescriptionDetail(id, setAlertMessages);
  };

  const contextValue = useMemo(
      () => ({
          prescriptionsList,
          selectedPrescription,
          limit,
          offset,
          alertMsgs,
          prescriptionDetail,
          prescriptionsLoading,
          getPrescriptionDetail,
          getPrescriptionList,
          setOffset,
          setLimit,
          setSelectedPrescription,
      }),
      [
          prescriptionsList,
          selectedPrescription,
          limit,
          offset,
          alertMsgs,
          prescriptionDetail,
          prescriptionsLoading,
      ]
  );

  return (
      <PrescriptionContext.Provider value={contextValue}>{children}</PrescriptionContext.Provider>
  );
};

export const usePrescriptions = () => {
  return useContext(PrescriptionContext);
};
